import React, { useState } from "react";
import { useTags, useCreateTag, useUpdateTag, useDeleteTag } from "@/hooks/useTags";
import { type Tag, type CreateTagData, type UpdateTagData } from "@/services/tags.service";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Skeleton } from "./ui/skeleton";
import { Plus, Edit, Trash2, Tag as TagIcon, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';

const PRESET_COLORS = [
  "#EF4444", // Red
  "#F59E0B", // Orange
  "#10B981", // Green
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#6B7280", // Gray
  "#14B8A6", // Teal
];

export function TagsTab() {
  const { t } = useTranslation();
  const { data: tagsData, isLoading: isLoadingTags } = useTags();
  const createTagMutation = useCreateTag();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  const [newTag, setNewTag] = useState<CreateTagData>({
    name: "",
    color: "#6B7280",
    description: "",
  });

  const [editTag, setEditTag] = useState<UpdateTagData>({
    name: "",
    color: "",
    description: "",
  });

  const tags = tagsData?.data?.tags || [];

  const handleCreateTag = () => {
    if (!newTag.name.trim()) {
      alert(t('common:x4.tagNameRequired', 'Tag-Name ist erforderlich'));
      return;
    }

    createTagMutation.mutate(newTag, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        setNewTag({ name: "", color: "#6B7280", description: "" });
      },
      onError: (error: any) => {
        alert(error?.response?.data?.message || "Tag konnte nicht erstellt werden");
      },
    });
  };

  const handleEditTag = () => {
    if (!selectedTag) return;

    if (editTag.name && !editTag.name.trim()) {
      alert(t('common:x4.tagNameNotEmpty', 'Tag-Name darf nicht leer sein'));
      return;
    }

    updateTagMutation.mutate(
      { id: selectedTag.id, data: editTag },
      {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setSelectedTag(null);
          setEditTag({ name: "", color: "", description: "" });
        },
        onError: (error: any) => {
          alert(error?.response?.data?.message || "Tag konnte nicht aktualisiert werden");
        },
      }
    );
  };

  const handleDeleteTag = () => {
    if (!selectedTag) return;

    deleteTagMutation.mutate(selectedTag.id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setSelectedTag(null);
      },
      onError: (error: any) => {
        alert(error?.response?.data?.message || "Tag konnte nicht gelöscht werden");
      },
    });
  };

  const openEditDialog = (tag: Tag) => {
    setSelectedTag(tag);
    setEditTag({
      name: tag.name,
      color: tag.color,
      description: tag.description || "",
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (tag: Tag) => {
    setSelectedTag(tag);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Main Card */}
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="flex flex-row items-center justify-between pb-2 border-b border-slate-100">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900">{t('ticket:ui.tags', 'Tags')}</h3>
              <span className="text-xs text-slate-400 font-normal ml-2">({tags.length} gesamt)</span>
            </div>
            <p className="text-sm text-slate-500">
              {t('common:x4.manageTagsDesc', 'Verwalten Sie Tags zur Kategorisierung und Priorisierung von Tickets.')}
            </p>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            size="sm"
            className="h-9 gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
          >
            <Plus className="h-4 w-4" />
            {t('common:x1.newTag', 'Neues Tag')}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {/* Tags List */}
          {isLoadingTags ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-3 w-[150px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : tags.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center text-slate-500">
              <TagIcon className="h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">
                {t('common:x4.noTagsCreated', 'Noch keine Tags erstellt')}
              </p>
              <p className="text-xs text-slate-400 max-w-xs mx-auto">
                {t('common:x4.createTagsToOrganize', 'Erstellen Sie Tags, um Ihre Tickets besser zu organisieren')}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCreateDialogOpen(true)}
                className="mt-2 h-8 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                {t('common:x4.createFirstTag', 'Erstes Tag erstellen')}
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50/50 text-xs font-medium text-slate-500 border-b border-slate-100">
                <div className="col-span-4">{t('common:x4.nameColor', 'Name & Farbe')}</div>
                <div className="col-span-4">{t('common:field.description', 'Beschreibung')}</div>
                <div className="col-span-3">{t('common:x4.createdBy', 'Erstellt von')}</div>
                <div className="col-span-1 text-right">{t('common:field.actions', 'Aktionen')}</div>
              </div>
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="group grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50/50 transition-colors"
                >
                  <div className="col-span-4 flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center ring-1 ring-slate-200/50"
                      style={{ backgroundColor: `${tag.color}15`, color: tag.color }}
                    >
                      <TagIcon className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-900">{tag.name}</h3>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider font-mono">{tag.color}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-span-4">
                    {tag.description ? (
                      <p className="text-sm text-slate-600 truncate pr-4" title={tag.description}>
                        {tag.description}
                      </p>
                    ) : (
                      <span className="text-sm text-slate-400 italic">{t('common:x4.noDescription', 'Keine Beschreibung')}</span>
                    )}
                  </div>
                  
                  <div className="col-span-3 flex flex-col justify-center">
                    <span className="text-sm text-slate-900">
                      {tag.created_by_first_name} {tag.created_by_last_name}
                    </span>
                    <span className="text-xs text-slate-400">
                      {t('common:x4.creator', 'Ersteller')}
                    </span>
                  </div>
                  
                  <div className="col-span-1 flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(tag)}
                      className="h-8 w-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-white"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(tag)}
                      className="h-8 w-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Tag Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-slate-900">{t('common:x1.newTag', 'Neues Tag')}</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Erstellen Sie ein neues Tag zur Kategorisierung von Tickets. Wählen Sie eine aussagekräftige Farbe.
                </p>
              </div>
              
              <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 block mb-2">{t('common:field.preview', 'Vorschau')}</span>
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 w-fit">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: newTag.color }} />
                  <span className="text-xs font-medium text-slate-700">{newTag.name || "Tag Name"}</span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 bg-white min-w-0">
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                <DialogTitle className="text-base font-semibold text-slate-900">
                  {t('common:x4.tagDetails', 'Tag-Details')}
                </DialogTitle>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6 max-w-lg">
                  <div className="space-y-2">
                    <Label htmlFor="tag-name" className="text-xs font-medium text-slate-700">{t('common:field.name', 'Name')}</Label>
                    <Input
                      id="tag-name"
                      placeholder={t('common:x1.egTagNames', 'z.B. Bug, Feature, Wichtig')}
                      value={newTag.name}
                      onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                      className="h-9 border-slate-200 focus-visible:ring-slate-200"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-xs font-medium text-slate-700">{t('common:action.selectColor', 'Farbe wählen')}</Label>
                    <div className="flex gap-2 flex-wrap">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={cn(
                            "w-8 h-8 rounded-full border-2 transition-all ring-offset-2 focus:ring-2 focus:outline-none",
                            newTag.color === color
                              ? "border-slate-900 scale-110 ring-slate-200"
                              : "border-transparent hover:scale-105"
                          )}
                          style={{ backgroundColor: color }}
                          onClick={() => setNewTag({ ...newTag, color })}
                        />
                      ))}
                      <div className="relative ml-2">
                        <Input
                          type="color"
                          value={newTag.color}
                          onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
                          className="w-8 h-8 p-0 border-0 rounded-full cursor-pointer overflow-hidden"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tag-description" className="text-xs font-medium text-slate-700">{t('common:field.descriptionOptional', 'Beschreibung (Optional)')}</Label>
                    <Textarea
                      id="tag-description"
                      placeholder={t('common:x1.tagUsedFor', 'Wofür wird dieses Tag verwendet?')}
                      value={newTag.description}
                      onChange={(e) => setNewTag({ ...newTag, description: e.target.value })}
                      rows={3}
                      className="resize-none border-slate-200 focus-visible:ring-slate-200 min-h-[80px]"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                  className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                >
                  {t('common:action.cancel', 'Abbrechen')}
                </Button>
                <Button
                  onClick={handleCreateTag}
                  disabled={createTagMutation.isPending || !newTag.name.trim()}
                  className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {createTagMutation.isPending ? "Wird erstellt..." : "Tag erstellen"}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tag Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-slate-900">{t('common:x4.editTag', 'Tag bearbeiten')}</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Ändern Sie die Eigenschaften des Tags. Die Änderungen werden sofort wirksam.
                </p>
              </div>
              
              <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                <span className="text-[10px] uppercase font-bold text-slate-400 block mb-2">{t('common:field.preview', 'Vorschau')}</span>
                <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 w-fit">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: editTag.color }} />
                  <span className="text-xs font-medium text-slate-700">{editTag.name || "Tag Name"}</span>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 bg-white min-w-0">
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                <DialogTitle className="text-base font-semibold text-slate-900">
                  {t('common:x4.adjustTag', 'Tag anpassen')}
                </DialogTitle>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6 max-w-lg">
                  <div className="space-y-2">
                    <Label htmlFor="edit-tag-name" className="text-xs font-medium text-slate-700">{t('common:field.name', 'Name')}</Label>
                    <Input
                      id="edit-tag-name"
                      placeholder={t('common:x1.egTagNames', 'z.B. Bug, Feature, Wichtig')}
                      value={editTag.name}
                      onChange={(e) => setEditTag({ ...editTag, name: e.target.value })}
                      className="h-9 border-slate-200 focus-visible:ring-slate-200"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-xs font-medium text-slate-700">{t('common:action.selectColor', 'Farbe wählen')}</Label>
                    <div className="flex gap-2 flex-wrap">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={cn(
                            "w-8 h-8 rounded-full border-2 transition-all ring-offset-2 focus:ring-2 focus:outline-none",
                            editTag.color === color
                              ? "border-slate-900 scale-110 ring-slate-200"
                              : "border-transparent hover:scale-105"
                          )}
                          style={{ backgroundColor: color }}
                          onClick={() => setEditTag({ ...editTag, color })}
                        />
                      ))}
                      <div className="relative ml-2">
                        <Input
                          type="color"
                          value={editTag.color}
                          onChange={(e) => setEditTag({ ...editTag, color: e.target.value })}
                          className="w-8 h-8 p-0 border-0 rounded-full cursor-pointer overflow-hidden"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-tag-description" className="text-xs font-medium text-slate-700">{t('common:field.description', 'Beschreibung')}</Label>
                    <Textarea
                      id="edit-tag-description"
                      placeholder={t('common:x1.tagUsedFor', 'Wofür wird dieses Tag verwendet?')}
                      value={editTag.description}
                      onChange={(e) => setEditTag({ ...editTag, description: e.target.value })}
                      rows={3}
                      className="resize-none border-slate-200 focus-visible:ring-slate-200 min-h-[80px]"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                  className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                >
                  {t('common:action.cancel', 'Abbrechen')}
                </Button>
                <Button
                  onClick={handleEditTag}
                  disabled={updateTagMutation.isPending}
                  className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {updateTagMutation.isPending ? "Wird gespeichert..." : "Änderungen speichern"}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md rounded-xl border border-white/70 bg-white/60 backdrop-blur-md ring-1 ring-black/5">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('common:x4.deleteTagQ', 'Tag löschen?')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-500 mt-2">
              {t('common:x4.theTag', 'Das Tag')} <strong className="text-slate-700">{selectedTag?.name}</strong> wird unwiderruflich gelöscht und von allen Tickets entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2">
            <AlertDialogCancel className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50">
              {t('common:action.cancel', 'Abbrechen')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTag}
              disabled={deleteTagMutation.isPending}
              className="h-9 rounded-lg bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteTagMutation.isPending ? "Löscht..." : "Unwiderruflich löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
